import React from 'react';
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { BeaconConcern, BeaconConcernType } from '../../api/services';

const TYPE_LABEL: Record<BeaconConcernType, string> = {
  unassigned_visitor: 'No Location Assigned',
  misassigned_beacon: 'Location Mismatch',
  lpa_config_gap:     'Missing LPA',
  sku_drift:          'SKU Drift',
};

const EVENT_LABEL: Record<string, string> = {
  opened:       'Opened',
  reopened:     'Reopened',
  acknowledged: 'Acknowledged',
  resolved:     'Resolved',
};

export interface ConcernDrawerProps {
  concern: BeaconConcern | null;
  locationLookup: Record<string, string>;
  onClose: () => void;
}

function formatTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export const ConcernDrawer: React.FC<ConcernDrawerProps> = ({ concern, locationLookup, onClose }) => {
  const open = concern !== null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 480 } } }}
    >
      {concern && (
        <>
          {/* Spacer below the fixed AppBar so the drawer header isn't covered. */}
          <Toolbar />
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, pr: 5 }}>
              {TYPE_LABEL[concern.concern_type]}
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                {concern.beacon_id}
              </Typography>
              <IconButton onClick={onClose} size="small" aria-label="Close concern drawer"
                          sx={{ position: 'absolute', top: 8, right: 8 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Box>

          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
            <SummarySection concern={concern} locationLookup={locationLookup} />
            <Divider sx={{ my: 2 }} />
            <TimelineSection concern={concern} />
            <Divider sx={{ my: 2 }} />
            <HistorySection concern={concern} />
          </Box>
        </>
      )}
    </Drawer>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{label}</Typography>
    <Box sx={{ wordBreak: 'break-word' }}>{children}</Box>
  </Box>
);

const SummarySection: React.FC<{ concern: BeaconConcern; locationLookup: Record<string, string> }> = ({ concern, locationLookup }) => {
  const observedAt = locationLookup[String(concern.location_id)] || `Location ${concern.location_id}`;
  const statusColor = concern.status === 'open'
    ? 'error'
    : concern.status === 'acknowledged'
    ? 'warning'
    : 'success';

  return (
    <Box>
      <Typography variant="overline" sx={{ color: 'text.secondary' }}>Summary</Typography>
      <Field label="Status">
        <Chip label={concern.status} size="small" color={statusColor} />
      </Field>
      <Field label="Observed at">{observedAt}</Field>
      <Field label="SKU">
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{concern.sku}</Typography>
      </Field>
      {concern.concern_type === 'misassigned_beacon' && concern.details?.assigned_location_id && (
        <Field label="Assigned to">
          {locationLookup[String(concern.details.assigned_location_id)] || `Location ${concern.details.assigned_location_id}`}
        </Field>
      )}
      {concern.concern_type === 'sku_drift' && concern.details?.bis_sku && (
        <Field label="BIS SKU">
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{concern.details.bis_sku}</Typography>
        </Field>
      )}
    </Box>
  );
};

const TimelineSection: React.FC<{ concern: BeaconConcern }> = ({ concern }) => (
  <Box>
    <Typography variant="overline" sx={{ color: 'text.secondary' }}>Timeline</Typography>
    <Field label="First detected">{formatTs(concern.first_detected_at)}</Field>
    <Field label="Last observed">{formatTs(concern.last_observed_at)}</Field>
    {concern.resolved_at && (
      <Field label="Resolved">
        {formatTs(concern.resolved_at)}
        {concern.resolution_mode && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {concern.resolution_mode}
          </Typography>
        )}
      </Field>
    )}
  </Box>
);

const HistorySection: React.FC<{ concern: BeaconConcern }> = ({ concern }) => {
  if (!concern.history || concern.history.length === 0) {
    return (
      <Box>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>History</Typography>
        <Typography variant="body2" color="text.secondary">No history events yet.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="overline" sx={{ color: 'text.secondary' }}>History</Typography>
      <Stack spacing={1.5} sx={{ mt: 0.5 }}>
        {concern.history.map((event, idx) => (
          <Box key={`${event.at}-${idx}`} sx={{ borderLeft: 2, borderColor: 'divider', pl: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {EVENT_LABEL[event.event] || event.event}
                {event.resolution_mode && (
                  <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                    ({event.resolution_mode})
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {formatTs(event.at)}
              </Typography>
            </Stack>
            {event.actor && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                by {event.actor}
              </Typography>
            )}
            {event.note && (
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {event.note}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
